"use client";

import { useEffect, useRef, useState, type DragEvent } from "react";
import {
  Eraser,
  ImagePlus,
  Paperclip,
  ShieldAlert,
  UploadCloud,
  X,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { MediaMosaic } from "@/components/ui/media-mosaic";
import { optimizeImageFiles } from "@/lib/media/optimize-image";
import { canRedactImageFile } from "@/lib/media/redact-image";
import { cn, formatFileSize } from "@/lib/utils";

type FileDropzoneProps = {
  id?: string;
  accept?: string;
  multiple?: boolean;
  files: File[];
  onFilesChange: (files: File[]) => void;
  title?: string;
  description?: string;
  compact?: boolean;
  imageOnly?: boolean;
  optimizeImages?: boolean;
  disabled?: boolean;
  className?: string;
  onRedactImage?: (index: number) => void;
  /** Betegadat / személyes adat figyelmeztetés feltöltés előtt. */
  privacyNotice?: boolean;
};

export function FileDropzone({
  id,
  accept,
  multiple = false,
  files,
  onFilesChange,
  title = "Húzza ide a fájlt",
  description = "vagy válassza ki a számítógépéről",
  compact = false,
  imageOnly = false,
  optimizeImages = true,
  disabled = false,
  className,
  onRedactImage,
  privacyNotice = false,
}: FileDropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [optimizing, setOptimizing] = useState(false);

  async function addFiles(fileList: FileList | null) {
    if (!fileList || disabled || optimizing) {
      return;
    }

    const selected = Array.from(fileList);
    setOptimizing(true);
    try {
      const prepared = optimizeImages
        ? await optimizeImageFiles(selected)
        : selected;
      onFilesChange(multiple ? [...files, ...prepared] : prepared.slice(0, 1));
    } finally {
      setOptimizing(false);
      if (inputRef.current) {
        inputRef.current.value = "";
      }
    }
  }

  function handleDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setDragging(false);
    addFiles(event.dataTransfer.files);
  }

  return (
    <div className={cn("space-y-2", className)}>
      {privacyNotice ? (
        <p
          role="note"
          className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-950"
        >
          <ShieldAlert className="mt-0.5 size-4 shrink-0 text-amber-700" />
          <span>
            Ne töltsön fel betegazonosításra alkalmas személyes vagy egészségügyi
            adatot. Ha a képen mégis szerepel ilyen információ, a feltöltés előtt
            takarja ki a beépített{" "}
            <span className="font-semibold">Kitakarás</span> eszközzel.
          </span>
        </p>
      ) : null}
      <div
        className={cn(
          "group flex cursor-pointer items-center rounded-xl border border-dashed bg-muted/30 transition-colors",
          compact ? "gap-3 px-3 py-2.5" : "flex-col justify-center px-5 py-6 text-center",
          dragging
            ? "border-primary bg-primary/5"
            : "hover:border-primary/50 hover:bg-primary/[0.03]",
          (disabled || optimizing) && "pointer-events-none opacity-50",
        )}
        role="button"
        tabIndex={disabled || optimizing ? -1 : 0}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            inputRef.current?.click();
          }
        }}
        onDragEnter={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragOver={(event) => event.preventDefault()}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
      >
        <span
          className={cn(
            "flex shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary",
            compact ? "size-9" : "mb-3 size-11",
          )}
        >
          {imageOnly ? (
            <ImagePlus className="size-5" />
          ) : compact ? (
            <Paperclip className="size-4" />
          ) : (
            <UploadCloud className="size-5" />
          )}
        </span>
        <span className={cn(compact && "min-w-0 flex-1 text-left")}>
          <span className="block text-sm font-medium">{title}</span>
          <span className="mt-0.5 block text-xs text-muted-foreground">
            {optimizing ? "Képek optimalizálása…" : description}
          </span>
        </span>
        {compact ? (
          <span className="text-xs font-semibold text-primary">Tallózás</span>
        ) : (
          <span className="mt-3 rounded-full border bg-card px-3 py-1.5 text-xs font-semibold text-primary shadow-sm">
            Fájl tallózása
          </span>
        )}
        <input
          ref={inputRef}
          id={id}
          type="file"
          accept={accept}
          multiple={multiple}
          disabled={disabled || optimizing}
          className="sr-only"
          onChange={(event) => addFiles(event.target.files)}
        />
      </div>

      {files.length > 0 ? (
        imageOnly ? (
          <MediaPreviewList
            files={files}
            onRemove={(index) =>
              onFilesChange(files.filter((_, itemIndex) => itemIndex !== index))
            }
            onRedactImage={onRedactImage}
          />
        ) : (
        <ul className="space-y-1.5">
          {files.map((file, index) => (
            <li
              key={`${file.name}-${file.lastModified}-${index}`}
              className="flex items-center gap-3 rounded-xl border bg-card px-3 py-2"
            >
              <Paperclip className="size-4 shrink-0 text-primary" />
              <span className="min-w-0 flex-1 truncate text-sm">{file.name}</span>
              <span className="shrink-0 text-xs text-muted-foreground">
                {formatFileSize(file.size)}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-7"
                aria-label={`${file.name} eltávolítása`}
                onClick={(event) => {
                  event.stopPropagation();
                  onFilesChange(files.filter((_, itemIndex) => itemIndex !== index));
                }}
              >
                <X className="size-3.5" />
              </Button>
            </li>
          ))}
        </ul>
        )
      ) : null}
    </div>
  );
}

function MediaPreviewList({
  files,
  onRemove,
  onRedactImage,
}: {
  files: File[];
  onRemove: (index: number) => void;
  onRedactImage?: (index: number) => void;
}) {
  const fileKey = files
    .map((file) => `${file.name}-${file.size}-${file.lastModified}`)
    .join("|");
  const [urls, setUrls] = useState<string[]>([]);

  useEffect(() => {
    const next = files.map((file) => URL.createObjectURL(file));
    setUrls(next);
    return () => {
      next.forEach((url) => URL.revokeObjectURL(url));
    };
    // fileKey captures the selected files without recreating previews on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileKey]);

  return (
    <MediaMosaic
      className="border"
      items={files.map((file, index) => ({
        key: `${file.name}-${file.lastModified}-${index}`,
        src: urls[index] ?? "",
        alt: file.name,
        kind: file.type.startsWith("video/") ? "video" : "image",
      }))}
      overlay={(item, index) => {
        const file = files[index];
        const canRedact =
          Boolean(onRedactImage) &&
          item.kind === "image" &&
          file != null &&
          canRedactImageFile(file);
        return (
          <div className="absolute top-2 right-2 flex items-center gap-1.5">
            {canRedact ? (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="h-7 gap-1 rounded-full bg-white/90 px-2.5 text-xs shadow-sm"
                aria-label={`${item.alt ?? "Kép"} kitakarása`}
                onClick={(event) => {
                  event.stopPropagation();
                  onRedactImage?.(index);
                }}
              >
                <Eraser className="size-3.5" />
                Kitakarás
              </Button>
            ) : null}
            <Button
              type="button"
              variant="secondary"
              size="icon"
              className="size-7 rounded-full bg-white/90 shadow-sm"
              aria-label={`${item.alt ?? "Fájl"} eltávolítása`}
              onClick={(event) => {
                event.stopPropagation();
                onRemove(index);
              }}
            >
              <X className="size-3.5" />
            </Button>
          </div>
        );
      }}
    />
  );
}
